import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { MIN_RESOLUTION, resolutionForWidth } from './telemetry-downsample';
import {
  BudgetInputs,
  BYTES_PER_POINT_HEAP,
  CONSTRAINED_DEVICE_MEMORY_GIB,
  DEFAULT_DEVICE_MEMORY_GIB,
  HEAP_SAFETY_FRACTION,
  MAX_BUFFERED_POINTS,
  MIN_TICK_RETENTION,
  BYTES_PER_POINT_ESTIMATE,
  DEFAULT_ROWS_PER_RESPONSE,
  DWELL_MS,
  MemorySignals,
  NetworkSignals,
  PressureAction,
  QUALITY_MODES,
  QualityMode,
  bufferedPoints,
  combineBudget,
  enforceBufferCap,
  memoryAllowance,
  pressureAction,
  retentionForRowWidth,
  seedGoodputBytesPerMs,
  throughputAllowance,
  updateEwma,
} from './adaptive-resolution';

/**
 * Adaptive downsampling controller.
 *
 * Owns the three live signals behind the point budget — connection throughput,
 * chart pixel width, browser memory — plus the manual quality override, and hands
 * `ChartViewportService` a single number where `resolutionForWidth` used to be
 * read directly. See ADAPTIVE_DOWNSAMPLING.md for the formulas and rationale.
 *
 * Two properties matter for how this is wired:
 *
 *   ONE INJECTION POINT   every request — backfill, poll, and the zoom refetch —
 *                         already resolves its budget through paramsFor(), so
 *                         replacing the number there covers all three at once.
 *   ONE MEASUREMENT POINT ProjectService is where every telemetry response lands,
 *                         so a single timing hook there feeds the EWMA without
 *                         components knowing this class exists.
 *
 * Every browser API is feature-detected. With all of them absent — Safari and
 * Firefox ship neither NetworkInformation nor performance.memory — the controller
 * degrades to exactly the item-3 behaviour: the pixel ceiling alone.
 */

/** A ring buffer the controller may measure and, under pressure, shrink. */
export interface AdaptiveBufferHandle {
  /** Stable name, for diagnostics and to replace a re-registered buffer. */
  readonly key: string;
  /** Rows currently retained. */
  retainedCount(): number;
  /** Points in one row — the width we last fetched at. */
  pointsPerRow(): number;
  /** Drop oldest rows until at most `retention` remain. */
  shrinkTo(retention: number): void;
}

/** The slice of TelemetryRingBuffer the registry needs. */
export interface ShrinkableBuffer<T> {
  readonly retainedCount: number;
  readonly last: T | undefined;
  shrinkTo(capacity: number): boolean;
}

/**
 * Wrap a ring buffer as a registry handle.
 *
 * Width is read from the NEWEST row rather than averaged, because that is the
 * budget currently in force — older rows may have been fetched at a different rung
 * and averaging them would lag the very change the cap is reacting to.
 *
 * Registration is explicit, and so is unregistration: a self-registering buffer
 * would keep a live reference to every buffer ever created, pinning exactly the
 * ~176 KB rows this whole subsystem exists to bound.
 */
export function ringBufferHandle<T>(
  key: string,
  buffer: ShrinkableBuffer<T>,
  widthOf: (row: T) => number
): AdaptiveBufferHandle {
  return {
    key,
    retainedCount: () => buffer.retainedCount,
    pointsPerRow: () => {
      const newest = buffer.last;
      return newest === undefined ? 0 : Math.max(0, widthOf(newest));
    },
    shrinkTo: (retention: number) => {
      buffer.shrinkTo(retention);
    },
  };
}

/** One completed telemetry response, as measured at the fetch chokepoint. */
export interface TelemetrySample {
  /** Wire bytes, or an estimate when the size is not observable. */
  bytes: number;
  /** Wall-clock milliseconds from request to parsed response. */
  elapsedMs: number;
  /** Rows the response carried. */
  rows: number;
  /** Points per row it was fetched at; 0 when full resolution. */
  resolution: number;
}

/** Everything the budget was computed from — for diagnostics and the UI. */
export interface BudgetExplanation {
  budget: number;
  pixelCeiling: number;
  throughputAllowance: number;
  memoryAllowance: number;
  quality: QualityMode;
  pressure: PressureAction;
  goodputBytesPerMs: number;
  bytesPerPoint: number;
  rowsPerResponse: number;
  bufferedPoints: number;
  overCap: boolean;
}

const QUALITY_STORAGE_KEY = 'vericure.charts.quality';

@Injectable({ providedIn: 'root' })
export class AdaptiveResolutionService {
  /**
   * EWMA of observed goodput, bytes per millisecond.
   *
   * Seeded LAZILY. Seeding in the constructor would call the overridable
   * `networkSignals()` before a subclass's fields exist — a hazard for any
   * subclass, and the reason this is null until first use rather than eager.
   */
  private goodputBytesPerMs: number | null = null;
  /** EWMA of observed JSON bytes per transmitted point. */
  private bytesPerPoint: number = BYTES_PER_POINT_ESTIMATE;
  /** EWMA of rows per response. */
  private rowsPerResponse: number = DEFAULT_ROWS_PER_RESPONSE;

  private quality: QualityMode = 'auto';
  private readonly qualitySource = new BehaviorSubject<QualityMode>('auto');
  readonly quality$: Observable<QualityMode> = this.qualitySource.asObservable();

  /** Per-family hysteresis: the rung in force, and when a pending rung appeared. */
  private held = new Map<string, { budget: number; pendingSince: number; pending: number }>();

  private buffers = new Map<string, AdaptiveBufferHandle>();

  /** Set when the cap forced a reduction, so budgetFor cannot undo it. */
  private capBudget: number | null = null;

  constructor() {
    this.quality = this.loadQuality();
    this.qualitySource.next(this.quality);
  }

  /** Goodput estimate, seeding from the connection on first use. */
  private goodput(): number {
    if (this.goodputBytesPerMs === null) {
      this.goodputBytesPerMs = seedGoodputBytesPerMs(this.networkSignals());
    }
    return this.goodputBytesPerMs;
  }

  // ── Manual override ───────────────────────────────────────────────────────

  getQuality(): QualityMode {
    return this.quality;
  }

  /**
   * Set the manual override and persist it.
   *
   * Persistence is DEVICE-scoped rather than user-scoped, and deliberately so: the
   * same operator on a slow site laptop and a fast office desktop wants different
   * answers, because what this setting really describes is the machine and the
   * link, not the person.
   */
  setQuality(mode: QualityMode): void {
    if (!QUALITY_MODES.includes(mode) || mode === this.quality) {
      return;
    }
    this.quality = mode;
    this.storeQuality(mode);
    // A deliberate quality change should be visible now, not after the dwell.
    this.held.clear();
    this.qualitySource.next(mode);
  }

  /**
   * Adopt a server-supplied quality, if the preferences document ever carries one.
   *
   * The `v1/preferences` contract has no quality field today, so nothing calls this
   * yet. It exists so that promoting the setting server-side is a one-field change
   * on each side rather than a rework: local stays the fallback, the server wins
   * when it speaks.
   */
  applyServerQuality(mode: string | null | undefined): void {
    if (typeof mode === 'string' && (QUALITY_MODES as readonly string[]).includes(mode)) {
      this.setQuality(mode as QualityMode);
    }
  }

  // ── Measurement ───────────────────────────────────────────────────────────

  /**
   * Record one completed response. Called from the ProjectService chokepoint.
   *
   * Rejects nonsense rather than letting it poison the averages: a zero elapsed
   * time (cached response) would read as infinite bandwidth, and a zero-row
   * response carries no width information at all.
   */
  recordSample(sample: TelemetrySample): void {
    const { bytes, elapsedMs, rows, resolution } = sample;
    if (!(bytes > 0) || !(elapsedMs > 0) || !(rows > 0)) {
      return;
    }

    this.goodputBytesPerMs = updateEwma(this.goodput(), bytes / elapsedMs);
    this.rowsPerResponse = updateEwma(this.rowsPerResponse, rows);

    if (resolution > 0) {
      // Only a downsampled response tells us the true per-point wire cost; a
      // full-res response's width is the fixed DTS array, not a budget we chose.
      this.bytesPerPoint = updateEwma(this.bytesPerPoint, bytes / (rows * resolution));
    }
  }

  // ── Buffer registry ───────────────────────────────────────────────────────

  registerBuffer(handle: AdaptiveBufferHandle): void {
    this.buffers.set(handle.key, handle);
  }

  unregisterBuffer(key: string): void {
    this.buffers.delete(key);
  }

  /** Points held across every registered buffer. */
  bufferedPoints(): number {
    return bufferedPoints(
      [...this.buffers.values()].map((b) => ({
        retainedCount: b.retainedCount(),
        pointsPerRow: b.pointsPerRow(),
      }))
    );
  }

  /** Rows held across every registered buffer. */
  retainedRowsTotal(): number {
    let total = 0;
    for (const buffer of this.buffers.values()) {
      total += Math.max(0, buffer.retainedCount());
    }
    return total;
  }

  /**
   * Enforce the hard cap. Returns the budget ceiling now in force, if any.
   *
   * Resolution is lowered first because it is reversible and only affects rows not
   * yet fetched; eviction runs only when even minimum-width rows will not fit,
   * because an evicted tick costs a refetch the moment the user scrubs back.
   */
  enforceCap(currentBudget: number): number | null {
    const points = this.bufferedPoints();
    const verdict = enforceBufferCap(
      points,
      currentBudget > 0 ? currentBudget : MIN_RESOLUTION,
      this.retainedRowsTotal()
    );
    if (!verdict.overCap) {
      this.capBudget = null;
      return null;
    }
    if (verdict.evictRatio !== null) {
      // Proportional, per buffer: each keeps the same fraction of its own rows, so
      // the result is correct whatever mix of widths and retentions is registered.
      for (const buffer of this.buffers.values()) {
        const keep = Math.floor(buffer.retainedCount() * verdict.evictRatio);
        buffer.shrinkTo(Math.max(MIN_TICK_RETENTION, keep));
      }
    }
    this.capBudget = verdict.reducedBudget;
    return this.capBudget;
  }

  // ── The budget ────────────────────────────────────────────────────────────

  /**
   * Point budget for one family's next request.
   *
   * A pixel width of 0 propagates as 0 — item 3's "not laid out yet, fetch full
   * resolution" signal — rather than being clamped up to MIN_RESOLUTION. Full res
   * is always correct, just larger, and that is the safe direction to fail in.
   */
  budgetFor(family: string, pixelWidth: number): number {
    const pixelCeiling = resolutionForWidth(pixelWidth);
    if (pixelCeiling <= 0) {
      return 0;
    }

    const memory = this.memorySignals();
    const pressure = pressureAction(memory);

    const inputs: BudgetInputs = {
      pixelCeiling,
      throughputAllowance: throughputAllowance({
        goodputBytesPerMs: this.goodput(),
        rttMs: this.networkSignals().rttMs,
        bytesPerPoint: this.bytesPerPoint,
        rowsPerResponse: this.rowsPerResponse,
      }),
      memoryAllowance: memoryAllowance({
        deviceMemoryGiB: memory.deviceMemoryGiB,
        retainedRowsTotal: this.retainedRowsTotal(),
      }),
      quality: this.quality,
      pressure,
    };

    let budget = combineBudget(inputs);

    const cap = this.enforceCap(budget);
    if (cap !== null) {
      budget = Math.min(budget, cap);
    }

    return this.applyHysteresis(family, budget, pressure);
  }

  /**
   * Retention for a buffer whose rows are `pointsPerRow` wide.
   *
   * Sized from the SAME memory budget the resolution ladder uses, so the two
   * cannot disagree about how much heap this tab may spend.
   */
  /**
   * Safari / iOS, which exposes neither deviceMemory nor performance.memory.
   *
   * Overridable so specs can state the platform they describe rather than
   * depending on the agent running them.
   */
  protected isConstrainedPlatform(): boolean {
    try {
      const ua = globalThis.navigator?.userAgent ?? '';
      const touchMac =
        /Macintosh/.test(ua) && (globalThis.navigator?.maxTouchPoints ?? 0) > 1;
      // iPadOS reports itself as a Mac; maxTouchPoints is what distinguishes it.
      return /iPad|iPhone|iPod/.test(ua) || touchMac;
    } catch {
      return false;
    }
  }

  retentionFor(pointsPerRow: number, bufferCount = 4): number {
    const memory = this.memorySignals();

    // MEASURE WHERE WE CAN; ASSUME CONSERVATIVELY WHERE WE CANNOT.
    //
    // navigator.deviceMemory is Chromium-only. Safari — every iPad, which is the
    // device this budget matters most for — does not expose it, so the old
    // fallback handed the most constrained target the SAME 4 GiB assumption as a
    // desktop and let it budget hundreds of megabytes. The budget was a guess
    // exactly where a guess is most expensive.
    //
    // Order of preference:
    //   1. performance.memory's real heap limit, when the browser reports one.
    //   2. A conservative constant for Safari / iOS, which is the platform that
    //      lacks both APIs and has the least headroom.
    //   3. The generic default, for anything else unknown.
    let byteBudget: number;
    if (
      typeof memory.heapLimitBytes === 'number' &&
      memory.heapLimitBytes > 0
    ) {
      byteBudget = memory.heapLimitBytes * HEAP_SAFETY_FRACTION;
    } else {
      const gib =
        typeof memory.deviceMemoryGiB === 'number' && memory.deviceMemoryGiB > 0
          ? memory.deviceMemoryGiB
          : this.isConstrainedPlatform()
          ? CONSTRAINED_DEVICE_MEMORY_GIB
          : DEFAULT_DEVICE_MEMORY_GIB;
      byteBudget = gib * 1024 ** 3 * HEAP_SAFETY_FRACTION;
    }

    byteBudget = Math.min(byteBudget, MAX_BUFFERED_POINTS * BYTES_PER_POINT_HEAP);
    return retentionForRowWidth(pointsPerRow, bufferCount, byteBudget);
  }

  /** The budget plus every term behind it. */
  explain(family: string, pixelWidth: number): BudgetExplanation {
    const memory = this.memorySignals();
    const network = this.networkSignals();
    const pixelCeiling = resolutionForWidth(pixelWidth);
    const points = this.bufferedPoints();
    return {
      budget: this.budgetFor(family, pixelWidth),
      pixelCeiling,
      throughputAllowance: throughputAllowance({
        goodputBytesPerMs: this.goodput(),
        rttMs: network.rttMs,
        bytesPerPoint: this.bytesPerPoint,
        rowsPerResponse: this.rowsPerResponse,
      }),
      memoryAllowance: memoryAllowance({
        deviceMemoryGiB: memory.deviceMemoryGiB,
        retainedRowsTotal: this.retainedRowsTotal(),
      }),
      quality: this.quality,
      pressure: pressureAction(memory),
      goodputBytesPerMs: this.goodput(),
      bytesPerPoint: this.bytesPerPoint,
      rowsPerResponse: this.rowsPerResponse,
      bufferedPoints: points,
      overCap: this.capBudget !== null,
    };
  }

  /** Forget learned state — project switch or a deliberate reset. */
  reset(): void {
    this.goodputBytesPerMs = null;
    this.bytesPerPoint = BYTES_PER_POINT_ESTIMATE;
    this.rowsPerResponse = DEFAULT_ROWS_PER_RESPONSE;
    this.held.clear();
    this.capBudget = null;
  }

  // ── Internals ─────────────────────────────────────────────────────────────

  /**
   * Hold a rung for DWELL_MS before moving UP; move DOWN immediately.
   *
   * The asymmetry is the point. Upward thrash re-requests the same window at
   * alternating resolutions and reads as flicker, so it can afford to wait.
   * Downward moves are the network or the heap telling us we are already in
   * trouble, and waiting there costs a stall or a dead tab.
   */
  private applyHysteresis(
    family: string,
    budget: number,
    pressure: PressureAction
  ): number {
    const current = this.held.get(family);
    const now = this.now();

    if (!current) {
      this.held.set(family, { budget, pendingSince: now, pending: budget });
      return budget;
    }

    if (budget <= current.budget || pressure !== 'none') {
      this.held.set(family, { budget, pendingSince: now, pending: budget });
      return budget;
    }

    if (budget !== current.pending) {
      // A new candidate rung — restart its dwell.
      this.held.set(family, { ...current, pending: budget, pendingSince: now });
      return current.budget;
    }

    if (now - current.pendingSince >= DWELL_MS) {
      this.held.set(family, { budget, pendingSince: now, pending: budget });
      return budget;
    }

    return current.budget;
  }

  /** Overridable for tests. */
  protected now(): number {
    return Date.now();
  }

  /** Feature-detected NetworkInformation. */
  protected networkSignals(): NetworkSignals {
    const connection = (
      globalThis.navigator as unknown as {
        connection?: { downlink?: number; effectiveType?: string; rtt?: number };
      }
    )?.connection;
    return {
      downlinkMbps: typeof connection?.downlink === 'number' ? connection.downlink : null,
      effectiveType:
        typeof connection?.effectiveType === 'string' ? connection.effectiveType : null,
      rttMs: typeof connection?.rtt === 'number' ? connection.rtt : null,
    };
  }

  /** Feature-detected deviceMemory + performance.memory. */
  protected memorySignals(): MemorySignals {
    const deviceMemory = (globalThis.navigator as unknown as { deviceMemory?: number })
      ?.deviceMemory;
    const heap = (
      globalThis.performance as unknown as {
        memory?: { usedJSHeapSize?: number; jsHeapSizeLimit?: number };
      }
    )?.memory;
    return {
      deviceMemoryGiB: typeof deviceMemory === 'number' ? deviceMemory : null,
      usedHeapBytes: typeof heap?.usedJSHeapSize === 'number' ? heap.usedJSHeapSize : null,
      heapLimitBytes:
        typeof heap?.jsHeapSizeLimit === 'number' ? heap.jsHeapSizeLimit : null,
    };
  }

  private loadQuality(): QualityMode {
    try {
      const stored = globalThis.localStorage?.getItem(QUALITY_STORAGE_KEY);
      if (stored && (QUALITY_MODES as readonly string[]).includes(stored)) {
        return stored as QualityMode;
      }
    } catch {
      // Private mode, or storage disabled — auto is the right fallback.
    }
    return 'auto';
  }

  private storeQuality(mode: QualityMode): void {
    try {
      globalThis.localStorage?.setItem(QUALITY_STORAGE_KEY, mode);
    } catch {
      // Non-fatal: the setting simply does not survive the session.
    }
  }
}
